import React, { useEffect } from "react";
import { X } from "lucide-react";

interface DashboardModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: "md" | "lg" | "xl";
}

const MODAL_MAX_WIDTH_CLASS = {
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
} as const;

export default function DashboardModal({
  title,
  onClose,
  children,
  maxWidth = "md",
}: DashboardModalProps) {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1200] overflow-y-auto bg-[#0d1117]/80 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={onClose}
    >
      <div className="mx-auto flex min-h-full w-full items-center justify-center">
        <div
          className={`my-auto flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-[#161b22] shadow-[0_8px_32px_rgba(0,0,0,0.4)] ${MODAL_MAX_WIDTH_CLASS[maxWidth]}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-modal-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-white/[0.06] px-5 py-4">
            <h3 id="dashboard-modal-title" className="text-base font-bold text-[#f0f6fc]">
              {title}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 text-[#8b949e] transition-colors hover:bg-white/[0.06] hover:text-[#f0f6fc]"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
